import React, { useState, useEffect, useMemo } from 'react';
import { PaperAirplaneIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { GasNomination } from '../types/gas';
import { gasNominationService } from '../services/gasService';
import CommonPageHeader from '../components/common/CommonPageHeader';
import LoadingScreen from '../components/common/LoadingScreen';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';

const GasNominations: React.FC = () => {
  const { t } = useLanguage();
  const [nominations, setNominations] = useState<GasNomination[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const loadNominations = async () => {
    setLoading(true);
    try {
      const data = await gasNominationService.getAllGasNominations();
      setNominations(data);
    } catch (error) {
      console.error('Error loading gas nominations:', error);
      toast.error(t('gas.messages.loadError', { name: t('gas.sections.nominations') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNominations();
  }, []);

  const filteredNominations = useMemo(() => {
    if (!searchTerm.trim()) return nominations;
    const term = searchTerm.toLowerCase();
    return nominations.filter(nomination =>
      nomination.gasDay?.includes(term) ||
      nomination.contractId?.toLowerCase().includes(term) ||
      nomination.customerId?.toLowerCase().includes(term) ||
      nomination.type?.toLowerCase().includes(term) ||
      nomination.status?.toLowerCase().includes(term)
    );
  }, [nominations, searchTerm]);

  const totalPages = Math.ceil(filteredNominations.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentNominations = filteredNominations.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getTypeLabel = (type?: string) => {
    switch (type) {
      case 'day_ahead': return 'Előző napi';
      case 'within_day': return 'Napon belüli';
      case 'long_term': return 'Hosszú távú';
      default: return type || '—';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'draft': return 'Vázlat';
      case 'submitted': return 'Beküldött';
      case 'confirmed': return 'Visszaigazolt';
      case 'rejected': return 'Elutasított';
      default: return status || '—';
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="space-y-4">
      <CommonPageHeader
        title={t('gas.sections.nominations')}
        subtitle={t('gas.list.subtitle', { count: nominations.length })}
        icon={PaperAirplaneIcon}
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('gas.list.search.nominations')}
          />
        </div>
        <button
          onClick={loadNominations}
          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4 mr-2" />
          {t('gas.list.refresh')}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.nomination.gasDay')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.nomination.type')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.nomination.quantityM3')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.nomination.quantityKwh')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.nomination.status')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.nomination.fields.notes')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentNominations.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                    {t('gas.list.noResults')}
                  </td>
                </tr>
              ) : (
                currentNominations.map(nomination => (
                  <tr key={nomination.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{nomination.gasDay || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">{getTypeLabel(nomination.type)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{nomination.quantityM3 ?? '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{nomination.quantityKwh ?? '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        nomination.status === 'confirmed'
                          ? 'bg-green-100 text-green-700'
                          : nomination.status === 'submitted'
                            ? 'bg-blue-100 text-blue-700'
                            : nomination.status === 'rejected'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                      }`}>
                        {getStatusLabel(nomination.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{nomination.notes || '—'}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={filteredNominations.length}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        indexOfFirstItem={indexOfFirstItem}
        indexOfLastItem={indexOfLastItem}
      />
    </div>
  );
};

export default GasNominations;
